import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Mail, ArrowLeft, CheckCircle, Key, Lock, Loader2, Check, X } from "lucide-react";
import { API_BASE } from "@/App";

// Helper to extract error message from API response
const getErrorMessage = (err, fallback = "Something went wrong.") => {
  if (!err.response?.data?.detail) return fallback;
  const detail = err.response.data.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || "Validation error").join(', ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};

/**
 * Password Recovery Page
 * 
 * Flow: Email → OTP Verification → Reset Password
 */
const ForgotPasswordPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [step, setStep] = useState("email"); // email, otp, reset, success
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  
  const otpRefs = useRef([]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Focus first OTP input
  useEffect(() => {
    if (step === "otp" && otpRefs.current[0]) {
      otpRefs.current[0].focus();
    }
  }, [step]);

  // Password validation
  const hasMinLength = password.length >= 8;
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=\[\]{}|;':,.<>?]/.test(password);
  const passwordsMatch = password === confirmPassword && password.length > 0;
  const isPasswordValid = hasMinLength && hasNumber && hasSpecial && passwordsMatch;

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, 6).split("");
      const newOtp = [...otp];
      digits.forEach((digit, i) => {
        if (index + i < 6) newOtp[index + i] = digit;
      });
      setOtp(newOtp);
      const lastIndex = Math.min(index + digits.length - 1, 5);
      if (otpRefs.current[lastIndex]) otpRefs.current[lastIndex].focus();
      return;
    }
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5 && otpRefs.current[index + 1]) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  // Request recovery OTP
  const requestOTP = async () => {
    if (!email) {
      setError("Please enter your email address.");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const response = await managedAxios.post(`${API_BASE}/auth/recovery/request-otp`, { email });
      if (response.data.success) {
        setResendTimer(response.data.resend_available_in || 60);
        setStep("otp");
        toast({ description: "Recovery OTP sent to your email." });
      }
    } catch (err) {
      setError(getErrorMessage(err, "Failed to send OTP. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  // Reset password with OTP
  const resetPassword = async () => {
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      setError("Please enter the complete 6-digit OTP.");
      return;
    }
    
    if (!isPasswordValid) {
      setError("Please ensure password meets all requirements.");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      await managedAxios.post(`${API_BASE}/auth/recovery/reset-password`, {
        email,
        otp: otpString,
        new_password: password
      });
      
      setStep("success");
      toast({ description: "Password reset successfully!" });
    } catch (err) {
      setError(getErrorMessage(err, "Failed to reset password. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const resendOTP = async () => {
    if (resendTimer > 0) return;
    
    setLoading(true);
    try {
      const response = await managedAxios.post(`${API_BASE}/auth/recovery/request-otp`, { email });
      if (response.data.success) {
        setResendTimer(response.data.resend_available_in || 60);
        setOtp(["", "", "", "", "", ""]);
        toast({ description: "New OTP sent to your email." });
      }
    } catch (err) {
      setError(getErrorMessage(err, "Failed to resend OTP."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 flex flex-col">
      {/* Back to Login */}
      <div className="p-6">
        <Link 
          to="/auth" 
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors bg-white/80 border border-slate-200/50 rounded-full px-4 py-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-20">
        <div className="w-full max-w-md">
          <Card className="bg-white border border-slate-200/50 shadow-lg rounded-2xl overflow-hidden">
            <CardContent className="p-8 space-y-6">
              
              {/* Step 1: Email */}
              {step === "email" && (
                <>
                  <div className="text-center space-y-2">
                    <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
                      <Mail className="w-7 h-7 text-slate-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-slate-900">Forgot Password?</h2>
                    <p className="text-sm text-slate-500">
                      No worries! Enter your email and we'll send you a recovery code.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Email</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(""); }}
                      placeholder="you@example.com"
                      className="h-12 border-slate-200 focus:border-indigo-400 rounded-xl"
                      onKeyDown={(e) => e.key === "Enter" && requestOTP()}
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
                  )}

                  <Button
                    onClick={requestOTP}
                    disabled={loading}
                    className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-full font-medium"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      "Send Recovery Code"
                    )}
                  </Button>
                </>
              )}

              {/* Step 2: OTP + New Password */}
              {step === "otp" && (
                <>
                  <div className="text-center space-y-2">
                    <div className="w-14 h-14 bg-indigo-50 rounded-full flex items-center justify-center mx-auto">
                      <Key className="w-7 h-7 text-indigo-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-slate-900">Verify & Reset</h2>
                    <p className="text-sm text-slate-500">
                      Enter the code sent to <span className="font-medium text-slate-700">{email}</span>
                    </p>
                  </div>

                  {/* OTP Input */}
                  <div className="flex justify-center gap-2">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (otpRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className="w-11 h-13 text-center text-lg font-bold border-2 border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                      />
                    ))}
                  </div>

                  {/* New Password */}
                  <div className="space-y-4 pt-4 border-t border-slate-100">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">New Password</Label>
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setError(""); }}
                        placeholder="Create a strong password"
                        className="h-11 border-slate-200 focus:border-indigo-400 rounded-xl"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">Confirm Password</Label>
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
                        placeholder="Confirm your password"
                        className="h-11 border-slate-200 focus:border-indigo-400 rounded-xl"
                      />
                    </div>

                    {/* Password Requirements */}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <RequirementItem met={hasMinLength} text="8+ characters" />
                      <RequirementItem met={hasNumber} text="One number" />
                      <RequirementItem met={hasSpecial} text="One special char" />
                      <RequirementItem met={passwordsMatch} text="Passwords match" />
                    </div>
                  </div>

                  {error && (
                    <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg text-center">{error}</p>
                  )}

                  <Button
                    onClick={resetPassword}
                    disabled={loading || otp.join("").length !== 6 || !isPasswordValid}
                    className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-full font-medium"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resetting...</>
                    ) : (
                      <><Lock className="w-4 h-4 mr-2" /> Reset Password</>
                    )}
                  </Button>

                  {/* Resend */}
                  <div className="text-center">
                    {resendTimer > 0 ? (
                      <p className="text-sm text-slate-500">
                        Resend code in <span className="font-medium">{resendTimer}s</span>
                      </p>
                    ) : (
                      <button
                        onClick={resendOTP}
                        disabled={loading}
                        className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        Resend code
                      </button>
                    )}
                  </div>

                  <button
                    onClick={() => { setStep("email"); setOtp(["", "", "", "", "", ""]); setError(""); }}
                    className="w-full text-sm text-slate-500 hover:text-slate-700"
                  >
                    ← Use a different email
                  </button>
                </>
              )}

              {/* Step 3: Success */}
              {step === "success" && (
                <div className="text-center space-y-4 py-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900">Password Reset!</h2>
                  <p className="text-sm text-slate-500">
                    Your password has been reset successfully.<br />
                    You can now login with your new password.
                  </p>
                  <Button
                    onClick={() => navigate("/auth")}
                    className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-full font-medium"
                  >
                    Go to Login
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Footer */}
          <p className="text-center text-sm text-slate-500 mt-6">
            Remember your password?{" "}
            <Link to="/auth" className="text-slate-900 font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const RequirementItem = ({ met, text }) => (
  <div className={`flex items-center gap-1.5 ${met ? "text-green-600" : "text-slate-400"}`}>
    {met ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
    <span>{text}</span>
  </div>
);

export default ForgotPasswordPage;
